import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useMemo, Children } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { BUTTON_SIZES, DEFAULT_BUTTON_SIZE, DEFAULT_BUTTON_VARIANT } from '../../constants.js';
import { useGetCurrentSizeClass } from '../../hooks/useGetCurrentSizeClass.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';
import { useTheme } from '../../hooks/useTheme.js';
import { convertVariantToValidCssClass } from '../../utils/slugHelpers.js';
import { getStartingSize } from '../../utils/styleHelpers.js';
import { _ as _toConsumableArray } from '../../unsorted/toConsumableArray.js';
import { isError } from '../../utils/textStyleHelpers.js';
import { hFlex, centerFlexChildren } from '../../utils/sxChunks.js';
import { _ as _taggedTemplateLiteral } from '../../unsorted/taggedTemplateLiteral.js';
import { keyframes } from '@emotion/react';
import { jsx } from '@emotion/react/jsx-runtime';
import { S as SvgIcon } from '../SvgIcon/SvgIcon.js';
import { f as flattenChildren } from '../../unsorted/index3.js';
import { isChildSubcomponent } from '../../utils/subcomponentHelpers.js';
import { B as BaseClickable } from '../BaseClickable/BaseClickable.js';
import { I as Icon } from '../Icon/Icon.js';
import { L as Logo } from '../Logo/Logo2.js';
import { B as Box } from '../Box/Box.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';

function ownKeys$8(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$8(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$8(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$8(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var smallButtonStaticStyles = function smallButtonStaticStyles(_ref) {
  var base = _ref.base;
  return {
    height: base.spacing.x8,
    fontSize: base.text.body.xSmall.regular.fontSize,
    fontWeight: base.text.body.medium.bold.fontWeight,
    fontFamily: base.font.family.body.primary,
    borderRadius: base.borderRadius.x15,
    paddingX: base.spacing.x3
  };
};
var mediumButtonStaticStyles = function mediumButtonStaticStyles(_ref2) {
  var base = _ref2.base;
  return {
    height: base.spacing.x12,
    fontSize: base.text.body.medium.regular.fontSize,
    fontWeight: base.text.body.medium.bold.fontWeight,
    fontFamily: base.font.family.body.primary,
    borderRadius: base.borderRadius.x15,
    paddingX: base.spacing.x4
  };
};
var largeButtonStaticStyles = function largeButtonStaticStyles(_ref3) {
  var base = _ref3.base;
  return {
    height: base.spacing.x16,
    fontSize: base.text.body.medium.regular.fontSize,
    fontWeight: base.text.body.medium.bold.fontWeight,
    fontFamily: base.font.family.body.primary,
    borderRadius: base.borderRadius.x20,
    paddingX: base.spacing.x5
  };
};
var baseButtonSx = {
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textDecoration: "none",
  transitionProperty: "box-shadow",
  transitionDuration: "base.motion.normal.fast.cssDuration",
  transitionTimingFunction: "base.motion.normal.fast.cssEase",
  // @NOTE: we seem to need this line of SX purely to stop iOS Safari
  // from displaying weird render artifacts on the button
  // https://immutable.atlassian.net/browse/DS-809
  translate: "0px",
  "&[disabled]": {
    cursor: "default"
  },
  "&.Button--large, &.Button--medium": {
    "& .Button__innerSpan__innerContent": {
      pl: "base.spacing.x3",
      "&:last-child": {
        pr: "base.spacing.x3"
      }
    },
    "& .Button__innerSpan__innerContent + .Button__iconOrLogo": {
      ml: "base.spacing.x3"
    }
  },
  "&.Button--small": {
    "& .Button__innerSpan__innerContent": {
      pl: "base.spacing.x2",
      "&:last-child": {
        pr: "base.spacing.x3"
      }
    },
    "& .Button__innerSpan__innerContent + .Button__iconOrLogo": {
      ml: "base.spacing.x2"
    }
  },
  "& .Button__innerSpan__innerContent + .Button__innerSpan__innerContent": {
    p: "0px"
  },
  "&.ButtCon .Button__innerSpan": _objectSpread$8(_objectSpread$8(_objectSpread$8({}, hFlex), centerFlexChildren), {}, {
    h: "100%",
    "& .Button__innerSpan__innerContent": _objectSpread$8(_objectSpread$8(_objectSpread$8({}, hFlex), centerFlexChildren), {}, {
      h: "100%",
      paddingLeft: "0px",
      paddingRight: "0px"
    })
  })
};

function ownKeys$7(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$7(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$7(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$7(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var primaryButtonGenericStyles = {
  background: "base.color.brand.1",
  color: "base.color.text.body.inverse.primary",
  boxShadow: "inset 0 0 0 0 transparent",
  transitionProperty: "none",
  flexShrink: 0,
  "&::before": {
    content: '""',
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    background: "base.color.brand.1",
    transitionProperty: "clip-path",
    transitionDuration: "base.motion.normal.fast.cssDuration",
    transitionTimingFunction: "base.motion.normal.fast.cssEase"
  }
};
var getPrimaryInverseButtonGenericStyles = function getPrimaryInverseButtonGenericStyles(_ref) {
  var base = _ref.base;
  return {
    background: base.colorMode === "onDark" ? "base.color.translucent.inverse.1000" : "base.color.neutral.500",
    color: "base.color.text.body.primary",
    "&::before": {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      background: base.colorMode === "onDark" ? "base.color.translucent.inverse.1000" : "base.color.neutral.500"
    }
  };
};
var getPrimaryFatalButtonGenericStyles = function getPrimaryFatalButtonGenericStyles(_ref2) {
  _ref2.base;
  return {
    background: "base.color.status.fatal.bright",
    "&::before": {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      background: "base.color.status.fatal.bright"
    }
  };
};
function getSmallPrimaryButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[100], " + ").concat(base.border.size[200], ")");
  var pressCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[200], ")");
  return _objectSpread$7(_objectSpread$7({}, smallButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x10, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.brand[1])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.brand[1]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x10, ")")
      }
    }
  });
}
function getMediumPrimaryButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[300], ")");
  var pressCutaway = "calc(".concat(base.border.size[300], " + ").concat(base.border.size[300], ")");
  return _objectSpread$7(_objectSpread$7({}, mediumButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x15, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.brand[1])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.brand[1]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x15, ")")
      }
    }
  });
}
function getLargePrimaryButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[400], ")");
  var pressCutaway = "calc(".concat(base.border.size[400], " + ").concat(base.border.size[400], ")");
  return _objectSpread$7(_objectSpread$7({}, largeButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x20, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.brand[1])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.brand[1]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x20, ")")
      }
    }
  });
}
function getSmallPrimaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[100], " + ").concat(base.border.size[200], ")");
  var pressCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[200], ")");
  return _objectSpread$7(_objectSpread$7({}, smallButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      background: "base.color.text.body.primary",
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x10, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright)
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x10, ")")
      }
    }
  });
}
function getMediumPrimaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[300], ")");
  var pressCutaway = "calc(".concat(base.border.size[300], " + ").concat(base.border.size[300], ")");
  return _objectSpread$7(_objectSpread$7({}, mediumButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x15, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright)
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x15, ")")
      }
    }
  });
}
function getLargePrimaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[400], ")");
  var pressCutaway = "calc(".concat(base.border.size[400], " + ").concat(base.border.size[400], ")");
  return _objectSpread$7(_objectSpread$7({}, largeButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x20, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.status.fatal.bright)
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x20, ")")
      }
    }
  });
}
function getSmallPrimaryInverseButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[100], " + ").concat(base.border.size[200], ")");
  var pressCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[200], ")");
  return _objectSpread$7(_objectSpread$7({}, smallButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      background: "base.color.text.body.primary",
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x10, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x10, ")")
      }
    }
  });
}
function getMediumPrimaryInverseButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[300], ")");
  var pressCutaway = "calc(".concat(base.border.size[300], " + ").concat(base.border.size[300], ")");
  return _objectSpread$7(_objectSpread$7({}, mediumButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x15, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x15, ")")
      }
    }
  });
}
function getLargePrimaryInverseButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc(".concat(base.border.size[200], " + ").concat(base.border.size[400], ")");
  var pressCutaway = "calc(".concat(base.border.size[400], " + ").concat(base.border.size[400], ")");
  return _objectSpread$7(_objectSpread$7({}, largeButtonStaticStyles(themeProps)), {}, {
    "&::before": {
      clipPath: "inset(".concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " ").concat(hoverCutaway, " round ").concat(base.borderRadius.x20, ")")
    },
    "&:hover:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500])
    },
    "&:active:not([disabled])": {
      background: "transparent",
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.colorMode === "onDark" ? base.color.translucent.inverse[1000] : base.color.neutral[500]),
      "&::before": {
        clipPath: "inset(".concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " ").concat(pressCutaway, " round ").concat(base.borderRadius.x20, ")")
      }
    }
  });
}
function getPrimaryButtonStyles(size, themeProps) {
  return merge(size === "small" ? getSmallPrimaryButtonUniqueStyles(themeProps) : size === "medium" ? getMediumPrimaryButtonUniqueStyles(themeProps) : getLargePrimaryButtonUniqueStyles(themeProps));
}
function getPrimaryFatalButtonStyles(size, themeProps) {
  return merge(size === "small" ? getSmallPrimaryFatalButtonUniqueStyles(themeProps) : size === "medium" ? getMediumPrimaryFatalButtonUniqueStyles(themeProps) : getLargePrimaryFatalButtonUniqueStyles(themeProps));
}
function getPrimaryInverseButtonStyles(size, themeProps) {
  return merge(size === "small" ? getSmallPrimaryInverseButtonUniqueStyles(themeProps) : size === "medium" ? getMediumPrimaryInverseButtonUniqueStyles(themeProps) : getLargePrimaryInverseButtonUniqueStyles(themeProps));
}

var _templateObject;
function ownKeys$6(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$6(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$6(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$6(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var fadeIn = keyframes(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  0% {\n    opacity: 0;\n    scale: 0.95;\n  }\n  100% {\n    opacity: 1;\n    scale: 1;\n  }\n"])));
var secondaryButtonGenericStyles = {
  color: "base.color.text.body.primary",
  flexShrink: 0,
  "&::before": {
    position: "absolute",
    top: "50%",
    left: "50%",
    translate: "-50% -50%",
    boxShadow: "inset 0 0 0 0px transparent",
    transitionProperty: "box-shadow",
    transitionDuration: "base.motion.normal.fast.cssDuration",
    transitionTimingFunction: "base.motion.normal.fast.cssEase",
    animationName: fadeIn,
    animationDuration: "base.motion.normal.fast.cssDuration",
    animationTimingFunction: "base.motion.normal.fast.cssEase"
  }
};
var secondaryFatalButtonGenericStyles = {
  color: "base.color.text.status.fatal.primary",
  flexShrink: 0,
  "&::before": {
    position: "absolute",
    boxShadow: "inset 0 0 0 0px transparent",
    top: "50%",
    left: "50%",
    translate: "-50% -50%",
    transitionProperty: "box-shadow",
    transitionDuration: "base.motion.normal.fast.cssDuration",
    transitionTimingFunction: "base.motion.normal.fast.cssEase",
    animationName: fadeIn,
    animationDuration: "base.motion.normal.fast.cssDuration",
    animationTimingFunction: "base.motion.normal.fast.cssEase"
  }
};
function getSmallSecondaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc((".concat(base.border.size[100], " + ").concat(base.border.size[200], ") * 2)");
  var pressCutaway = "calc((".concat(base.border.size[100], " + ").concat(base.border.size[200], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, smallButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright),
    "&::before": {
      borderRadius: base.borderRadius.x15
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[100], " ").concat(base.color.status.fatal.bright)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright)
      }
    }
  });
}
function getMediumSecondaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc((".concat(base.border.size[200], " + ").concat(base.border.size[300], ") * 2)");
  var pressCutaway = "calc((".concat(base.border.size[200], " + ").concat(base.border.size[300], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, mediumButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright),
    "&::before": {
      borderRadius: base.borderRadius.x20
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[100], " ").concat(base.color.status.fatal.bright)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright)
      }
    }
  });
}
function getLargeSecondaryFatalButtonUniqueStyles(themeProps) {
  var base = themeProps.base;
  var hoverCutaway = "calc((".concat(base.border.size[200], " + ").concat(base.border.size[400], ") * 2)");
  var pressCutaway = "calc((".concat(base.border.size[200], " + ").concat(base.border.size[400], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, largeButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(base.border.size[300], " ").concat(base.color.status.fatal.bright),
    "&::before": {
      borderRadius: base.borderRadius.x25
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[200], " ").concat(base.color.status.fatal.bright)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.status.fatal.bright),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(base.border.size[400], " ").concat(base.color.status.fatal.bright)
      }
    }
  });
}
function getSmallSecondaryButtonUniqueStyles(themeProps) {
  var hoverCutaway = "calc((".concat(themeProps.base.border.size[100], " + ").concat(themeProps.base.border.size[200], ") * 2)");
  var pressCutaway = "calc((".concat(themeProps.base.border.size[100], " + ").concat(themeProps.base.border.size[200], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, smallButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[200], " ").concat(themeProps.base.color.brand[1]),
    "&::before": {
      borderRadius: themeProps.base.borderRadius.x15
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[200], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[100], " ").concat(themeProps.base.color.brand[1])
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[200], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[200], " ").concat(themeProps.base.color.brand[1])
      }
    }
  });
}
function getMediumSecondaryButtonUniqueStyles(themeProps) {
  var hoverCutaway = "calc((".concat(themeProps.base.border.size[200], " + ").concat(themeProps.base.border.size[300], ") * 2)");
  var pressCutaway = "calc((".concat(themeProps.base.border.size[200], " + ").concat(themeProps.base.border.size[300], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, mediumButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[300], " ").concat(themeProps.base.color.brand[1]),
    "&::before": {
      borderRadius: themeProps.base.borderRadius.x20
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[300], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[100], " ").concat(themeProps.base.color.brand[1])
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[300], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[300], " ").concat(themeProps.base.color.brand[1])
      }
    }
  });
}
function getLargeSecondaryButtonUniqueStyles(themeProps) {
  var hoverCutaway = "calc((".concat(themeProps.base.border.size[200], " + ").concat(themeProps.base.border.size[400], ") * 2)");
  var pressCutaway = "calc((".concat(themeProps.base.border.size[200], " + ").concat(themeProps.base.border.size[400], ") * 2)");
  return _objectSpread$6(_objectSpread$6({}, largeButtonStaticStyles(themeProps)), {}, {
    boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[300], " ").concat(themeProps.base.color.brand[1]),
    "&::before": {
      borderRadius: themeProps.base.borderRadius.x25
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[400], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[200], " ").concat(themeProps.base.color.brand[1])
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[400], " ").concat(themeProps.base.color.brand[1]),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(themeProps.base.border.size[400], " ").concat(themeProps.base.color.brand[1])
      }
    }
  });
}
function getSecondaryButtonStyles(size, themeProps) {
  return merge(size === "small" ? getSmallSecondaryButtonUniqueStyles(themeProps) : size === "medium" ? getMediumSecondaryButtonUniqueStyles(themeProps) : getLargeSecondaryButtonUniqueStyles(themeProps));
}
function getSecondaryFatalButtonStyles(size, themeProps) {
  return merge(size === "small" ? getSmallSecondaryFatalButtonUniqueStyles(themeProps) : size === "medium" ? getMediumSecondaryFatalButtonUniqueStyles(themeProps) : getLargeSecondaryFatalButtonUniqueStyles(themeProps));
}

function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var tertiaryButtonGenericStyles = {
  color: "base.color.text.body.primary",
  transitionDelay: "0.06s",
  background: "base.color.translucent.standard.200",
  flexShrink: 0,
  "&::before": {
    position: "absolute",
    top: "50%",
    left: "50%",
    translate: "-50% -50%",
    boxShadow: "inset 0 0 0 0px transparent",
    transitionProperty: "box-shadow",
    transitionDuration: "base.motion.normal.fast.cssDuration",
    transitionTimingFunction: "base.motion.normal.fast.cssEase"
  }
};
var tertiaryInverseButtonGenericStyles = _objectSpread$5(_objectSpread$5({}, tertiaryButtonGenericStyles), {}, {
  color: "base.color.text.body.inverse.primary",
  background: "base.color.translucent.inverse.200",
  flexShrink: 0,
  "&::before": _objectSpread$5({}, tertiaryButtonGenericStyles["&::before"])
});
function getSmallTertiaryButtonUniqueStyles(theme, inverse) {
  var hoverCutaway = "calc((".concat(theme.base.border.size[100], " + ").concat(theme.base.border.size[200], ") * 2)");
  var pressCutaway = "calc((".concat(theme.base.border.size[100], " + ").concat(theme.base.border.size[200], ") * 2)");
  return _objectSpread$5(_objectSpread$5({}, smallButtonStaticStyles(theme)), {}, {
    "&::before": {
      borderRadius: theme.base.borderRadius.x15
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[200], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[100], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[200], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[200], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    }
  });
}
function getMediumTertiaryButtonUniqueStyles(theme, inverse) {
  var hoverCutaway = "calc((".concat(theme.base.border.size[200], " + ").concat(theme.base.border.size[300], ") * 2)");
  var pressCutaway = "calc((".concat(theme.base.border.size[200], " + ").concat(theme.base.border.size[300], ") * 2)");
  return _objectSpread$5(_objectSpread$5({}, mediumButtonStaticStyles(theme)), {}, {
    "&::before": {
      borderRadius: theme.base.borderRadius.x20
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[300], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[100], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[300], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[300], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    }
  });
}
function getLargeTertiaryButtonUniqueStyles(theme, inverse) {
  var hoverCutaway = "calc((".concat(theme.base.border.size[200], " + ").concat(theme.base.border.size[400], ") * 2)");
  var pressCutaway = "calc((".concat(theme.base.border.size[200], " + ").concat(theme.base.border.size[400], ") * 2)");
  return _objectSpread$5(_objectSpread$5({}, largeButtonStaticStyles(theme)), {}, {
    "&::before": {
      borderRadius: theme.base.borderRadius.x25
    },
    "&:hover:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[400], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(hoverCutaway, ")"),
        height: "calc(100% - ".concat(hoverCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[200], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    },
    "&:active:not([disabled])": {
      boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[400], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary),
      "&::before": {
        content: '""',
        width: "calc(100% - ".concat(pressCutaway, ")"),
        height: "calc(100% - ".concat(pressCutaway, ")"),
        boxShadow: "inset 0 0 0 ".concat(theme.base.border.size[400], " ").concat(inverse ? theme.base.color.text.body.inverse.primary : theme.base.color.text.body.primary)
      }
    }
  });
}
function getTertiaryButtonStyles(size, themeProps, inverse) {
  return size === "small" ? getSmallTertiaryButtonUniqueStyles(themeProps, inverse) : size === "medium" ? getMediumTertiaryButtonUniqueStyles(themeProps, inverse) : getLargeTertiaryButtonUniqueStyles(themeProps, inverse);
}

var baseButtonInnerSpanStyles = {
  position: "relative",
  c: "inherit",
  whiteSpace: "nowrap"
};
function getResponsiveButtonStyles(_ref) {
  var size = _ref.size,
    variant = _ref.variant,
    themeProps = _ref.themeProps;
  var sizeAsArray = Array.isArray(size) ? _toConsumableArray(size) : [size];
  sizeAsArray.shift();
  return merge.apply(void 0, _toConsumableArray(sizeAsArray.map(function (responsiveSize, index) {
    if (responsiveSize != null && !isError(responsiveSize)) {
      var _themeProps$base$brea;
      var mediaStyleRule = "@media screen and (min-width: ".concat((_themeProps$base$brea = themeProps.base.breakpointAsArray) === null || _themeProps$base$brea === void 0 ? void 0 : _themeProps$base$brea[index], "px)");
      var styles = {};
      switch (variant) {
        case "secondary":
          styles = getSecondaryButtonStyles(responsiveSize, themeProps);
          break;
        case "tertiary":
          styles = getTertiaryButtonStyles(responsiveSize, themeProps);
          break;
        case "tertiary/inverse":
          styles = getTertiaryButtonStyles(responsiveSize, themeProps, true);
          break;
        case "primary/fatal":
          styles = getPrimaryFatalButtonStyles(responsiveSize, themeProps);
          break;
        case "secondary/fatal":
          styles = getSecondaryFatalButtonStyles(responsiveSize, themeProps);
          break;
        case "primary/inverse":
          styles = getPrimaryInverseButtonStyles(responsiveSize, themeProps);
          break;
        case "primary":
        default:
          styles = getPrimaryButtonStyles(responsiveSize, themeProps);
          break;
      }
      return _defineProperty({}, mediaStyleRule, styles);
    }
    return {};
  })));
}
function getDefaultButtonStyles(_ref3) {
  var size = _ref3.size,
    variant = _ref3.variant,
    themeProps = _ref3.themeProps;
  switch (variant) {
    case "secondary":
      return merge({}, baseButtonSx, getSecondaryButtonStyles(size, themeProps), secondaryButtonGenericStyles);
    case "secondary/fatal":
      return merge({}, baseButtonSx, getSecondaryFatalButtonStyles(size, themeProps), secondaryFatalButtonGenericStyles);
    case "tertiary/inverse":
      return merge({}, baseButtonSx, getTertiaryButtonStyles(size, themeProps, true), tertiaryInverseButtonGenericStyles);
    case "tertiary":
      return merge({}, baseButtonSx, getTertiaryButtonStyles(size, themeProps), tertiaryButtonGenericStyles);
    case "primary/fatal":
      return merge({}, baseButtonSx, primaryButtonGenericStyles, getPrimaryFatalButtonStyles(size, themeProps), getPrimaryFatalButtonGenericStyles(themeProps));
    case "primary/inverse":
      {
        return merge({}, baseButtonSx, primaryButtonGenericStyles, getPrimaryInverseButtonStyles(size, themeProps), getPrimaryInverseButtonGenericStyles(themeProps));
      }
    case "primary":
    default:
      return merge({}, baseButtonSx, primaryButtonGenericStyles, getPrimaryButtonStyles(size, themeProps));
  }
}
var getButtConIconStyles = function getButtConIconStyles(_ref4) {
  var variant = _ref4.variant,
    size = _ref4.size,
    base = _ref4.themeProps.base;
  return {
    width: size === "small" ? base.icon.size[200] : size === "medium" ? base.icon.size[250] : base.icon.size[300],
    fill: variant.includes("primary/inverse") ? base.color.text.body.primary : variant.includes("primary") ? base.color.brand[2] : variant.includes("secondary/fatal") ? base.color.status.fatal.bright : variant.includes("secondary") ? base.color.text.body.primary : variant.includes("tertiary/inverse") ? base.color.text.body.inverse.primary : base.color.text.body.primary
  };
};
var getButtConStyles = function getButtConStyles(_ref5) {
  var size = _ref5.size,
    themeProps = _ref5.themeProps;
  return {
    width: size === "small" ? themeProps.base.spacing.x8 : size === "medium" ? themeProps.base.spacing.x12 : themeProps.base.spacing.x16,
    minWidth: size === "small" ? themeProps.base.spacing.x8 : size === "medium" ? themeProps.base.spacing.x12 : themeProps.base.spacing.x16,
    paddingLeft: "0px",
    paddingRight: "0px"
  };
};
function getResponsiveButtConStyles(_ref6) {
  var size = _ref6.size,
    themeProps = _ref6.themeProps;
  var sizeAsArray = Array.isArray(size) ? _toConsumableArray(size) : [size];
  sizeAsArray.shift();
  var sizeStyles = merge.apply(void 0, _toConsumableArray(sizeAsArray.map(function (responsiveSize, index) {
    if (responsiveSize != null && !isError(responsiveSize)) {
      var _themeProps$base$brea2;
      var mediaStyleRule = "@media screen and (min-width: ".concat((_themeProps$base$brea2 = themeProps.base.breakpointAsArray) === null || _themeProps$base$brea2 === void 0 ? void 0 : _themeProps$base$brea2[index], "px)");
      return _defineProperty({}, mediaStyleRule, getButtConStyles({
        size: responsiveSize,
        themeProps: themeProps
      }));
    }
    return {};
  })));
  return sizeStyles;
}
function getResponsiveButtConIconStyles(_ref8) {
  var variant = _ref8.variant,
    size = _ref8.size,
    themeProps = _ref8.themeProps;
  var sizeAsArray = Array.isArray(size) ? _toConsumableArray(size) : [size];
  sizeAsArray.shift();
  var sizeStyles = merge.apply(void 0, _toConsumableArray(sizeAsArray.map(function (responsiveSize, index) {
    if (responsiveSize != null && !isError(responsiveSize)) {
      var _themeProps$base$brea3;
      var mediaStyleRule = "@media screen and (min-width: ".concat((_themeProps$base$brea3 = themeProps.base.breakpointAsArray) === null || _themeProps$base$brea3 === void 0 ? void 0 : _themeProps$base$brea3[index], "px)");
      return _defineProperty({}, mediaStyleRule, getButtConIconStyles({
        size: responsiveSize,
        themeProps: themeProps,
        variant: variant
      }));
    }
    return {};
  })));
  return sizeStyles;
}
function getButtonIconOrLogoSizeStyles(size, themeProps) {
  return {
    width: size === "small" ? themeProps.base.icon.size[200] : themeProps.base.icon.size[250],
    verticalAlign: "middle"
  };
}
function getButtonIconVariantStyles(variant, _ref10) {
  var base = _ref10.base;
  var fill = "";
  switch (variant) {
    case "primary/fatal":
      fill = base.color.text.body.inverse.primary;
      break;
    case "primary/inverse":
      fill = base.color.text.body.primary;
      break;
    case "primary":
      fill = base.color.text.body.inverse.primary;
      break;
    case "tertiary/inverse":
      fill = base.color.text.body.inverse.primary;
      break;
    case "secondary/fatal":
      fill = base.color.status.fatal.bright;
      break;
    case "secondary":
    case "tertiary":
    default:
      fill = base.color.text.body.primary;
      break;
  }
  return {
    fill: fill
  };
}
function getResponsiveButtonIconOrLogoStyles(_ref11) {
  var size = _ref11.size,
    variant = _ref11.variant,
    themeProps = _ref11.themeProps;
  var sizeAsArray = Array.isArray(size) ? _toConsumableArray(size) : [size];
  var defaultSize = sizeAsArray.shift();
  var sizeStyles = merge.apply(void 0, _toConsumableArray(sizeAsArray.map(function (responsiveSize, index) {
    if (responsiveSize != null && !isError(responsiveSize)) {
      var _themeProps$base$brea4;
      var mediaStyleRule = "@media screen and (min-width: ".concat((_themeProps$base$brea4 = themeProps.base.breakpointAsArray) === null || _themeProps$base$brea4 === void 0 ? void 0 : _themeProps$base$brea4[index], "px)");
      return _defineProperty({}, mediaStyleRule, getButtonIconOrLogoSizeStyles(responsiveSize, themeProps));
    }
    return {};
  })));
  var variantAsArray = Array.isArray(variant) ? _toConsumableArray(variant) : [variant];
  var defaultVariant = variantAsArray.shift();
  var variantStyles = merge.apply(void 0, _toConsumableArray(variantAsArray.map(function (responsiveVariant, index) {
    if (responsiveVariant != null && !isError(responsiveVariant)) {
      var _themeProps$base$brea5;
      var mediaStyleRule = "@media screen and (min-width: ".concat((_themeProps$base$brea5 = themeProps.base.breakpointAsArray) === null || _themeProps$base$brea5 === void 0 ? void 0 : _themeProps$base$brea5[index], "px)");
      return _defineProperty({}, mediaStyleRule, getButtonIconVariantStyles(responsiveVariant, themeProps));
    }
    return {};
  })));
  return {
    defaultSize: defaultSize,
    defaultVariant: defaultVariant,
    styles: merge({}, sizeStyles, variantStyles)
  };
}

var _excluded$4 = ["size", "variant", "domRef", "children", "sx"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ButtConSvgIcon(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_BUTTON_SIZE : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BUTTON_VARIANT : _ref$variant;
    _ref.domRef;
    var children = _ref.children,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$4);
  var themeProps = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);
  var allIconSx = merge(getButtConIconStyles({
    size: startingSize,
    variant: variant,
    themeProps: themeProps
  }), getResponsiveButtConIconStyles({
    variant: variant,
    size: size,
    themeProps: themeProps
  }), _objectSpread$4(_objectSpread$4(_objectSpread$4({}, sx), sx.w ? {
    width: sx.w
  } : sx.width ? {
    width: sx.width
  } : {}), sx.h ? {
    height: sx.w
  } : sx.height ? {
    height: sx.height
  } : {}));
  return jsx(SvgIcon, _objectSpread$4(_objectSpread$4({}, props), {}, {
    sx: allIconSx,
    children: children
  }));
}
ButtConSvgIcon.displayName = "ButtCon.SvgIcon";

var _excluded$3 = ["sx", "variant", "size"],
  _excluded2$1 = ["iconVariant"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ButtonIcon(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "primary" : _ref$variant,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "medium" : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$3);
  var _ref2 = "iconVariant" in props ? props : _objectSpread$3(_objectSpread$3({}, props), {}, {
      iconVariant: undefined
    }),
    iconVariant = _ref2.iconVariant,
    otherProps = _objectWithoutProperties(_ref2, _excluded2$1);
  var themeProps = useTheme();
  var _getResponsiveButtonI = getResponsiveButtonIconOrLogoStyles({
      size: size,
      variant: variant,
      themeProps: themeProps
    }),
    defaultSize = _getResponsiveButtonI.defaultSize,
    defaultVariant = _getResponsiveButtonI.defaultVariant,
    responsiveStyles = _getResponsiveButtonI.styles;
  var allStyles = useMemo(function () {
    return merge(getButtonIconOrLogoSizeStyles(defaultSize, themeProps), getButtonIconVariantStyles(defaultVariant, themeProps), responsiveStyles, sx);
  }, [defaultSize, defaultVariant, responsiveStyles, sx, themeProps]);
  var allProps = iconVariant ? _objectSpread$3(_objectSpread$3({}, otherProps), {}, {
    variant: iconVariant
  }) : otherProps;
  return jsx(Icon, _objectSpread$3(_objectSpread$3({}, allProps), {}, {
    sx: allStyles
  }));
}
ButtonIcon.displayName = "Button.Icon";

var _excluded$2 = ["sx", "size", "variant", "logo"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ButtonLogo(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "medium" : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "primary" : _ref$variant,
    logo = _ref.logo,
    props = _objectWithoutProperties(_ref, _excluded$2);
  var themeProps = useTheme();
  var _getResponsiveButtonI = getResponsiveButtonIconOrLogoStyles({
      size: size,
      variant: variant,
      themeProps: themeProps
    }),
    defaultSize = _getResponsiveButtonI.defaultSize,
    defaultVariant = _getResponsiveButtonI.defaultVariant,
    responsiveStyles = _getResponsiveButtonI.styles;
  var allStyles = useMemo(function () {
    return merge(getButtonIconOrLogoSizeStyles(defaultSize, themeProps), getButtonIconVariantStyles(defaultVariant, themeProps), responsiveStyles, sx);
  }, [defaultSize, defaultVariant, responsiveStyles, sx, themeProps]);
  return jsx(Logo, _objectSpread$2(_objectSpread$2({}, props), {}, {
    sx: allStyles,
    logo: logo
  }));
}
ButtonLogo.displayName = "Button.Logo";

var _excluded$1 = ["children", "sx", "variant", "size", "testId", "className"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var INTERNAL_CONTAINER_RC = jsx("span", {});
function Button(_ref) {
  var _themeProps$component, _themeProps$component2;
  var children = _ref.children,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "primary" : _ref$variant,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_BUTTON_SIZE : _ref$size,
    testId = _ref.testId,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var themeProps = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);
  var allContainerSx = merge(getDefaultButtonStyles({
    size: startingSize,
    variant: variant,
    themeProps: themeProps
  }), getResponsiveButtonStyles({
    size: size,
    variant: variant,
    themeProps: themeProps
  }), (_themeProps$component = (_themeProps$component2 = themeProps.components) === null || _themeProps$component2 === void 0 || (_themeProps$component2 = _themeProps$component2.Button) === null || _themeProps$component2 === void 0 ? void 0 : _themeProps$component2.sxOverride) !== null && _themeProps$component !== void 0 ? _themeProps$component : {}, sx);
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  return jsx(BaseClickable, _objectSpread$1(_objectSpread$1({}, props), {}, {
    sx: allContainerSx,
    testId: testId,
    className: "".concat(className !== null && className !== void 0 ? className : "", " Button Button--").concat(convertVariantToValidCssClass(variant), " Button--").concat(currentSizeClass),
    children: jsx(Box, {
      sx: baseButtonInnerSpanStyles,
      rc: INTERNAL_CONTAINER_RC,
      className: "Button__innerSpan",
      children: Children.map(flattenedChildren, function (child) {
        if (isChildSubcomponent(child, ButtonIcon) || isChildSubcomponent(child, ButtonLogo)) {
          var _child$props$classNam;
          // @NOTE: get sub-component children, with some special extra props
          return jsx(SmartClone, {
            size: size,
            variant: variant,
            className: "".concat((_child$props$classNam = child.props.className) !== null && _child$props$classNam !== void 0 ? _child$props$classNam : "", " Button__iconOrLogo"),
            testId: child.props.testId ? child.props.testId : "".concat(testId, "__icon"),
            children: child
          });
        }
        return jsx(Box, {
          rc: INTERNAL_CONTAINER_RC,
          className: "Button__innerSpan__innerContent",
          children: child
        });
      })
    })
  }));
}
Button.displayName = "Button";
Button.Icon = ButtonIcon;
Button.Logo = ButtonLogo;

var _excluded = ["size", "variant", "sx", "iconSx", "testId", "className"],
  _excluded2 = ["icon"],
  _excluded3 = ["children"],
  _excluded4 = ["iconVariant"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function ButtCon(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_BUTTON_SIZE : _ref$size,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BUTTON_VARIANT : _ref$variant,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$iconSx = _ref.iconSx,
    iconSx = _ref$iconSx === void 0 ? {} : _ref$iconSx,
    testId = _ref.testId,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var _ref2 = "icon" in props ? props : _objectSpread({
      icon: undefined
    }, props),
    icon = _ref2.icon,
    propsMinusIcon = _objectWithoutProperties(_ref2, _excluded2);
  var _ref3 = "children" in propsMinusIcon ? propsMinusIcon : _objectSpread({
      children: null
    }, propsMinusIcon),
    children = _ref3.children,
    propsMinusIconAndChildren = _objectWithoutProperties(_ref3, _excluded3);
  var _ref4 = "iconVariant" in propsMinusIconAndChildren ? propsMinusIconAndChildren : _objectSpread({
      iconVariant: undefined
    }, propsMinusIconAndChildren),
    iconVariant = _ref4.iconVariant,
    propsMinusIconAndChildrenAndIconVariant = _objectWithoutProperties(_ref4, _excluded4);
  var themeProps = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_BUTTON_SIZE, BUTTON_SIZES);
  var allButtonSx = useMemo(function () {
    var _themeProps$component, _themeProps$component2;
    return merge(getButtConStyles({
      size: startingSize,
      themeProps: themeProps
    }), getResponsiveButtConStyles({
      size: size,
      themeProps: themeProps
    }), (_themeProps$component = (_themeProps$component2 = themeProps.components) === null || _themeProps$component2 === void 0 || (_themeProps$component2 = _themeProps$component2.ButtCon) === null || _themeProps$component2 === void 0 ? void 0 : _themeProps$component2.sxOverride) !== null && _themeProps$component !== void 0 ? _themeProps$component : {}, sx);
  }, [size, startingSize, themeProps, sx]);
  var allIconSx = useMemo(function () {
    return merge(getButtConIconStyles({
      size: startingSize,
      variant: variant,
      themeProps: themeProps
    }), getResponsiveButtConIconStyles({
      variant: variant,
      size: size,
      themeProps: themeProps
    }), iconSx);
  }, [size, startingSize, variant, themeProps, iconSx]);
  var svgIconChild = useGetSubcomponentChild(children, ButtConSvgIcon);
  return jsx(Button, _objectSpread(_objectSpread({}, propsMinusIconAndChildrenAndIconVariant), {}, {
    size: size,
    variant: variant,
    sx: allButtonSx,
    testId: testId,
    className: "".concat(className !== null && className !== void 0 ? className : "", " ButtCon ButtCon--").concat(convertVariantToValidCssClass(variant), " ButtCon--").concat(currentSizeClass),
    children: svgIconChild ? jsx(SmartClone, {
      testId: "".concat(testId, "__svgIcon"),
      children: svgIconChild
    }) : icon ? jsx(Icon, {
      className: "buttCon__icon",
      sx: allIconSx,
      icon: icon,
      variant: iconVariant,
      testId: "".concat(testId, "__icon")
    }) : children
  }));
}
ButtCon.displayName = "ButtCon";
ButtCon.SvgIcon = ButtConSvgIcon;

export { ButtCon as B, Button as a, ButtConSvgIcon as b, ButtonIcon as c, ButtonLogo as d, getTertiaryButtonStyles as g };
//# sourceMappingURL=ButtCon.js.map
